import React, { useState } from 'react';
import { List, Avatar, Button, Menu, Dropdown, Tag, Modal, Space, Typography, Tabs, Input } from 'antd';
import { QuestionCircleOutlined, BookOutlined, EllipsisOutlined, TagOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// 유틸리티 함수: 문자열을 컬러 코드로 변환
const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
        '#1890ff', '#52c41a', '#faad14', '#f5222d',
        '#722ed1', '#13c2c2', '#eb2f96', '#fadb14'
    ];
    return colors[Math.abs(hash) % colors.length];
};



const formatTime = (seconds) => {
    // ISO 문자열을 Date 객체로 변환 후 유닉스 타임스탬프(밀리초)를 초로 변환
    const unixSeconds = Math.floor(new Date(seconds).getTime());

    const hours = Math.floor(unixSeconds / 3600);
    const minutes = Math.floor((unixSeconds % 3600) / 60);
    const secs = Math.floor(unixSeconds % 60);

    let result = '';
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0 || hours > 0) result += `${minutes}m `;
    result += `${secs}s`;

    return result.trim();
};


const { Text } = Typography;
const { TabPane } = Tabs;

// TranscriptItem 컴포넌트
const TranscriptItem = React.memo(({ item, handleAskAI, transcriptTags, onAddTag, onDeleteTag }) => {
    const [showOriginal, setShowOriginal] = useState(false);
    const [noteContent, setNoteContent] = useState('');
    const [isAddingNote, setIsAddingNote] = useState(false);
    const { t } = useTranslation();
    const hasTranslation = item.translation && item.translation.trim() !== '';
    const messageTags = transcriptTags[item.id] || [];

    const TAG_ICONS = {
        important: '📌',
        action_item: '✅',
        unclear: '❓',
        note: '📝'
    };

    const TAG_LABELS = {
        important: 'Important',
        action_item: 'Action Item',
        unclear: 'Unclear',
        note: 'Note'
    };

    const handleAddTag = (tagType) => {
        if (tagType === 'note') {
            setIsAddingNote(true);
        } else {
            onAddTag(item.id, tagType);
        }
    };

    const handleNoteSubmit = () => {
        if (noteContent.trim()) {
            onAddTag(item.id, 'note', noteContent);
            setNoteContent('');
        }
        setIsAddingNote(false);
    };

    const renderTags = () => {
        if (messageTags.length === 0) return null;

        return (
            <div style={{ marginTop: '8px' }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                    <Space wrap size={4}>
                        {messageTags.map((tag) => (
                            <Tag
                                key={tag.id}
                                color={tag.tag_type === 'note' ? 'blue' : 'default'}
                                closable
                                onClose={(e) => {
                                    e.preventDefault();
                                    onDeleteTag(item.id, tag.id);
                                }}
                            >
                                {TAG_ICONS[tag.tag_type]} {t(TAG_LABELS[tag.tag_type])}
                            </Tag>
                        ))}
                    </Space>
                    {messageTags.map((tag) => (
                        tag.tag_type === 'note' && (
                            <div key={`note-${tag.id}`} style={{
                                backgroundColor: '#f5f5f5',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                marginTop: '4px',
                                position: 'relative'
                            }}>
                                <Button
                                    type="text"
                                    size="small"
                                    danger
                                    style={{
                                        position: 'absolute',
                                        right: '8px',
                                        top: '8px'
                                    }}
                                    onClick={() => onDeleteTag(item.id, tag.id)}
                                >
                                    ✕
                                </Button>
                                <Text type="secondary">📝 {t('Note')}:</Text>
                                <div style={{ marginTop: '4px', paddingRight: '24px' }}>
                                    {tag.note_content}
                                </div>
                            </div>
                        )
                    ))}
                </Space>
            </div>
        );
    };

    const menu = (
        <Menu style={{ width: '200px', padding: '8px 0' }}>
            <Menu.SubMenu
                key="add-tag"
                title={<span><TagOutlined style={{ marginRight: '8px' }} />{t("Add Tag")}</span>}
                style={{ fontSize: '16px', padding: '4px 16px' }}
            >
                <Menu.Item key="important" onClick={() => handleAddTag('important')}>
                    {TAG_ICONS.important} {t("Mark as Important")}
                </Menu.Item>
                <Menu.Item key="action_item" onClick={() => handleAddTag('action_item')}>
                    {TAG_ICONS.action_item} {t("Add Action Item")}
                </Menu.Item>
                <Menu.Item key="unclear" onClick={() => handleAddTag('unclear')}>
                    {TAG_ICONS.unclear} {t("Mark as Unclear")}
                </Menu.Item>
                <Menu.Item key="note" onClick={() => handleAddTag('note')}>
                    {TAG_ICONS.note} {t("Add Note")}
                </Menu.Item>
            </Menu.SubMenu>

        </Menu>
    );

    return (
        <List.Item style={{ display: 'flex', alignItems: 'flex-start', padding: '12px 0' }}>
            <div style={{ marginRight: '16px', textAlign: 'center', width: '60px' }}>
                <Avatar size={40} style={{ backgroundColor: stringToColor(item.speaker_id) }}>
                    {item.speaker_id[0]}
                </Avatar>
                <div style={{ marginTop: '4px', fontSize: '10px', fontWeight: 'bold' }}>{item.speaker_id}</div>
                <div style={{ fontSize: '10px', color: '#888' }}>{formatTime(item.start_time)}</div>
            </div>
            <div style={{ flex: 1, backgroundColor: '#f0f0f0', padding: '10px', borderRadius: '8px' }}>
                <Text>{!hasTranslation || showOriginal ? item.text : item.translation}</Text>
                {hasTranslation && (
                    <Button
                        type="link"
                        size="small"
                        onClick={() => setShowOriginal(!showOriginal)}
                        style={{ padding: '4px', marginLeft: '8px' }}
                    >
                        {showOriginal ? t("Show Translation") : t("Show Original")}
                    </Button>
                )}
                {renderTags()}
            </div>
            <div style={{ marginLeft: '16px' }}>
                <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
                    <Button icon={<EllipsisOutlined />} style={{ fontSize: '20px', padding: '4px 8px' }} />
                </Dropdown>
            </div>
            {isAddingNote && (
                <Modal
                    title={t("Add Note")}
                    visible={isAddingNote}
                    onOk={handleNoteSubmit}
                    onCancel={() => {
                        setIsAddingNote(false);
                        setNoteContent('');
                    }}
                >
                    <Input.TextArea
                        value={noteContent}
                        onChange={(e) => setNoteContent(e.target.value)}
                        placeholder={t("Enter your note here...")}
                        rows={4}
                    />
                </Modal>
            )}
        </List.Item>
    );
});

// TranscriptList 컴포넌트
const TranscriptList = React.memo(({ transcriptions, handleAskAI, transcriptTags, onAddTag, onDeleteTag }) => {
    const transcriptionsWithId = transcriptions.map(item => ({
        ...item,
        id: item.uuid
    }));

    return (
        <List
            style={{
                height: '100%',
                overflow: 'auto',
                padding: '0 16px'
            }}
            dataSource={transcriptionsWithId.filter(item => item.text.trim() !== '')}
            renderItem={item => (
                <TranscriptItem
                    item={item}
                    handleAskAI={handleAskAI}
                    transcriptTags={transcriptTags}
                    onAddTag={onAddTag}
                    onDeleteTag={onDeleteTag}
                />
            )}
        />
    );
});

// 메인 TranscriptTab 컴포넌트
const TranscriptTab = React.memo(({
    memoizedTranscriptions,
    memoizedFilteredTranscriptions,
    handleAskAIMemoized,
    transcriptTags,
    onAddTag,
    onDeleteTag
}) => {
    const { t } = useTranslation();

    return (
        <Tabs
            defaultActiveKey="all"
            type="card"
            size="small"
            style={{ marginBottom: '16px' }}
        >
            <TabPane tab={t("All Messages")} key="all">
                <TranscriptList
                    transcriptions={memoizedTranscriptions}
                    handleAskAI={handleAskAIMemoized}
                    transcriptTags={transcriptTags}
                    onAddTag={onAddTag}
                    onDeleteTag={onDeleteTag}
                />
            </TabPane>
            <TabPane tab={`📌 ${t("Important")} (${memoizedFilteredTranscriptions.important.length})`} key="important">
                <TranscriptList
                    transcriptions={memoizedFilteredTranscriptions.important}
                    handleAskAI={handleAskAIMemoized}
                    transcriptTags={transcriptTags}
                    onAddTag={onAddTag}
                    onDeleteTag={onDeleteTag}
                />
            </TabPane>
            <TabPane tab={`✅ ${t("Action Items")} (${memoizedFilteredTranscriptions.action_item.length})`} key="action_item">
                <TranscriptList
                    transcriptions={memoizedFilteredTranscriptions.action_item}
                    handleAskAI={handleAskAIMemoized}
                    transcriptTags={transcriptTags}
                    onAddTag={onAddTag}
                    onDeleteTag={onDeleteTag}
                />
            </TabPane>
            <TabPane tab={`❓ ${t("Unclear")} (${memoizedFilteredTranscriptions.unclear.length})`} key="unclear">
                <TranscriptList
                    transcriptions={memoizedFilteredTranscriptions.unclear}
                    handleAskAI={handleAskAIMemoized}
                    transcriptTags={transcriptTags}
                    onAddTag={onAddTag}
                    onDeleteTag={onDeleteTag}
                />
            </TabPane>
            <TabPane tab={`📝 ${t("Notes")} (${memoizedFilteredTranscriptions.note.length})`} key="note">
                <TranscriptList
                    transcriptions={memoizedFilteredTranscriptions.note}
                    handleAskAI={handleAskAIMemoized}
                    transcriptTags={transcriptTags}
                    onAddTag={onAddTag}
                    onDeleteTag={onDeleteTag}
                />
            </TabPane>
        </Tabs>
    );
});

export default TranscriptTab; 